import axios from 'axios';
import Config from '../Config';
import LinkManager from './LinkManager';
import FavoritesCache from './FavoritesCache';
import {
    IconListItem,
    MentalFooterMenuLayout,
    PageLayout,
    IconListItemInfo,
    MentalDetail,
    MentalLinkIdentifier,
    Identifier,
    LessonSection,
    MultipartLessonDetail,
    MultipartLessonSection,
    LessonData,
    WritingLessonData,
    AudioLessonData,
    LessonLinkIdentifier,
    LessonLinkWithTitle,
    WindowLayout,
    MentalKnowHowMainData,
    MentalKnowHowTextData,
    UserData,
    VideoLessonData,
} from '../types';
import { getSlugFromTitle, getHTMLParagraphs } from './HelperFunctions';
import MappingHelper from './MentalApiMappingHelper';

class MentalApi {
    data: any;

    private findMentalData(mentalId: Identifier): any {
        const stringId = String(mentalId);
        return this.data.mentals.find((mental: any) => (String(mental.mental_id) === stringId));
    }

    private static findLessonDataInSections(sections: Array<any>, lessonId: Identifier): any {
        if (!sections) return null;

        const stringLessonId = String(lessonId);

        for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
            const lessons = sections[sectionIndex].lessons || [];

            const match = lessons.find((lesson: any) => String(lesson.id) === stringLessonId);

            if (match) {
                return match;
            }
        }

        return null;
    }

    private findLessonData(mentalId: Identifier, lessonId: Identifier): any {
        const mental = this.findMentalData(mentalId);

        if (!mental) return null;

        const sections: Array<any> = mental.lessons_section;

        return MentalApi.findLessonDataInSections(sections, lessonId);
    }

    private findSubLessonData(
        mentalId: Identifier,
        parentLessonId: Identifier,
        lessonId: Identifier,
    ): any {
        const parentLessonData = this.findLessonData(mentalId, parentLessonId);

        if (!parentLessonData) return null;

        const sections: Array<any> = parentLessonData.lessons_section_multilesson;

        return MentalApi.findLessonDataInSections(sections, lessonId);
    }

    getUserLevelIds(): Array<string> {
        if (this.data.user && Array.isArray(this.data.user.level_roleArr)) {
            return this.data.user.level_roleArr;
        }

        return [];
    }

    getUserData(): UserData | undefined {
        const { user } = this.data;

        if (!user || user.userID === 'no') {
            return undefined;
        }

        return {
            userId: user.userID,
            levels: this.getUserLevelIds().sort(),
        };
    }

    findLessonById(lessonId: string): LessonLinkWithTitle | undefined {
        if (!lessonId) return undefined;

        const stringLessonId = String(lessonId);

        for (let mentalIndex = 0; mentalIndex < this.data.mentals.length; mentalIndex += 1) {
            const mentalData = this.data.mentals[mentalIndex];

            const sections: Array<any> = mentalData.lessons_section || [];

            for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
                const { lessons } = sections[sectionIndex];

                for (let lessonIndex = 0; lessonIndex < lessons.length; lessonIndex += 1) {
                    const lessonData = lessons[lessonIndex];

                    if (String(lessonData.id) === stringLessonId) {
                        return {
                            link: LinkManager.getSingleLessonLink(
                                {
                                    mentalId: mentalData.mental_id,
                                    mentalSlug: getSlugFromTitle(mentalData.mental_titel),
                                },
                                lessonData.id,
                                getSlugFromTitle(lessonData.titel),
                            ),
                            title: lessonData.titel,
                        };
                    }

                    if (lessonData.art === 'mehrfach') {
                        const subSections: Array<any> = lessonData.lessons_section_multilesson;
                        const subLesson = MentalApi.findLessonDataInSections(
                            subSections,
                            stringLessonId,
                        );

                        if (subLesson) {
                            return {
                                link: LinkManager.getMultipartLessonSingleLessonLink(
                                    {
                                        mentalId: mentalData.mental_id,
                                        mentalSlug: getSlugFromTitle(mentalData.mental_titel),
                                    },
                                    lessonData.id,
                                    getSlugFromTitle(lessonData.titel),
                                    subLesson.id,
                                    getSlugFromTitle(subLesson.titel),
                                ),
                                title: subLesson.titel,
                            };
                        }
                    }
                }
            }
        }

        return undefined;
    }

    getFavoritesItem(): IconListItem | null {
        const { favoriten } = this.data;
        const title = 'Favoriten';

        if (!favoriten.on) return null;

        return {
            id: 0,
            title,
            iconType: 'icon',
            iconUrl: favoriten.icon,
            fontColor: favoriten.info_icon_color,
            info: {
                title,
                infoText: favoriten.info_text,
                infoIconColor: favoriten.info_icon_color,
                infoDialogLayout: MappingHelper.mapInfoWindowLayout(favoriten),
            },
            link: LinkManager.getFavoritesLink(),
        };
    }

    getMentalLessonIndex(mentalLinkIdentifier: MentalLinkIdentifier): MentalDetail | undefined {
        const mental = this.findMentalData(mentalLinkIdentifier.mentalId);

        if (!mental) {
            return undefined;
        }

        if (MappingHelper.mentalIsLocked(mental)) {
            return undefined;
        }

        let currentLessionSectionId: number = 0;
        const infoWindowLayout = MappingHelper.mapInfoWindowLayout(mental);
        const infoIconColor = mental.info_icon_color;
        const lessonSections = mental.lessons_section || [];

        return {
            id: mental.mental_id,
            title: mental.mental_titel,
            iconUrl: mental.mental_icon,
            infoWindowLayout,
            lessonSections: lessonSections.map((sectionData: any): LessonSection => {
                currentLessionSectionId += 1;

                return {
                    id: currentLessionSectionId,
                    title: sectionData.titel,
                    subtitle: sectionData.sub_titel,
                    layout: MappingHelper.mapLessonSectionLayout(sectionData.layout),
                    lessonItems: MappingHelper.mapLessonItems(
                        mentalLinkIdentifier,
                        infoWindowLayout,
                        infoIconColor,
                        sectionData.lessons,
                    ),
                };
            }),
        };
    }

    getSingleLesson(
        mental: MentalLinkIdentifier,
        parentLesson: LessonLinkIdentifier | undefined,
        lessonId: Identifier,
    ): LessonData | undefined {
        let lessonData;
        let parentLink;
        let titlePrefix = '';

        if (parentLesson) {
            lessonData = this.findSubLessonData(mental.mentalId, parentLesson.lessonId, lessonId);
            parentLink = LinkManager.getMultipartLessonIndexLink(parentLesson);
            const parentLessonData = this.findLessonData(mental.mentalId, parentLesson.lessonId);

            if (parentLessonData) {
                titlePrefix = `${parentLessonData.titel} - `;
            }
        } else {
            lessonData = this.findLessonData(mental.mentalId, lessonId);
            parentLink = LinkManager.getMentalLessonIndexLink(mental);
        }

        if (!lessonData || MappingHelper.lessonIsLocked(lessonData)) {
            return undefined;
        }

        const mentalData = this.findMentalData(mental.mentalId);
        if (!mentalData) {
            return undefined;
        }

        const mentalTitle = mentalData.mental_titel;

        const { id } = lessonData;
        const title = `${titlePrefix}${lessonData.titel}`;
        const iconUrl = lessonData.icon;

        if (lessonData.art === 'schrift') {
            const pdf = lessonData.pdf ? lessonData.pdf : undefined;

            const linkedAudioLesson = this.findLessonById(lessonData.audio_lesson_link);

            const writingLesson: WritingLessonData = {
                art: 'schrift',
                id,
                title,
                parentLink,
                mentalTitle,
                iconUrl,
                text: lessonData.text,
                pdf,
                linkedAudioLesson,
            };

            return writingLesson;
        }

        if (lessonData.art === 'video') {
            const videoLesson: VideoLessonData = {
                art: 'video',
                id,
                title,
                mentalTitle,
                parentLink,
                vimeoId: lessonData.vimeo_id,
            };

            return videoLesson;
        }

        const streams = MappingHelper
            .mapStreams(lessonData.streams_hq, lessonData.streams_lq)
            .filter(stream => stream.urlsHq
                && stream.urlsLq
                && stream.urlsHq.length > 0
                && stream.urlsLq.length > 0);

        const cachedFavorite = FavoritesCache.getFavoriteStatus(id);
        const isFavorit = cachedFavorite !== undefined
            ? cachedFavorite
            : lessonData.is_favorit;

        const audioLesson: AudioLessonData = {
            art: 'audio',
            id,
            title,
            parentLink,
            mentalTitle,
            isFavorit,
            streams,
            voiceNames: lessonData.voiceNames,
            playerLayout: this.getPlayerLayout(mental.mentalId),
        };

        return audioLesson;
    }

    getMultipartLesson(
        mental: MentalLinkIdentifier,
        lessonId: Identifier,
    ): MultipartLessonDetail | undefined {
        const mentalData = this.findMentalData(mental.mentalId);
        const lessonData = this.findLessonData(mental.mentalId, lessonId);

        if (!mentalData || !lessonData) {
            return undefined;
        }

        const { id } = lessonData;
        const title = lessonData.titel;
        const slug = getSlugFromTitle(title);

        let currentLessionSectionId: number = 0;
        const lessonSections: Array<any> = lessonData.lessons_section_multilesson || [];

        return {
            id,
            title,
            openingText: lessonData.opening_text,
            bodyColor: {
                bgColor: mentalData.info_window_layout.body_bg_color,
                fontColor: mentalData.info_window_layout.body_font_color,
            },
            lessonSections: lessonSections.map((sectionData: any): MultipartLessonSection => {
                currentLessionSectionId += 1;

                return {
                    id: currentLessionSectionId,
                    title: sectionData.title,
                    layout: MappingHelper.mapLessonSectionLayout(sectionData.layout),
                    lessons: MappingHelper.mapMultipartLessonItems(
                        mental,
                        id,
                        slug,
                        sectionData.lessons,
                        Config.pageUrls.lockedItemRedirect,
                    ),
                };
            }),
        };
    }

    getBusinessMentalItems(): Array<IconListItem> {
        if (!this.data.business || String(this.data.business.on) !== '1') {
            return [];
        }

        return [
            MappingHelper.mapBusiness(this.data.business),
        ];
    }

    getMentalsIndex(): Array<IconListItem> {
        const business = this.getBusinessMentalItems();
        const mentalsData = (this.data.mentals || []);

        const mentals: Array<IconListItem> = mentalsData.map((mental: any): IconListItem => {
            const id = mental.mental_id;
            const title = mental.mental_titel;
            const slug = getSlugFromTitle(title);

            const mentalIsLocked = Boolean(mental.mentals_locked_redirect);

            let info: IconListItemInfo | undefined;
            if (mental.info_text) {
                info = {
                    title,
                    infoIconColor: mental.info_icon_color,
                    infoText: mental.info_text,
                    infoDialogLayout: MappingHelper.mapInfoWindowLayout(mental),
                };
            }

            const link = mentalIsLocked
                ? mental.mentals_locked_redirect
                : LinkManager.getMentalLessonIndexLink({ mentalId: id, mentalSlug: slug });

            return {
                id,
                title,
                iconType: 'icon',
                iconUrl: mental.mental_icon,
                fontColor: mental.info_icon_color,
                info,
                link,
            };
        });

        return [
            ...business,
            ...mentals,
        ];
    }

    getKnowHowText(mentalLinkIdentifier: MentalLinkIdentifier, textIndex: number):
        MentalKnowHowTextData | undefined {
        const mentalData = this.findMentalData(mentalLinkIdentifier.mentalId);

        if (!mentalData) {
            return undefined;
        }

        const knowHowData = mentalData.know_how;

        if (!knowHowData
            || !Array.isArray(mentalData.tipps_long)
            || mentalData.tipps_long.length < textIndex) {
            return undefined;
        }

        const tip = mentalData.tipps_long[textIndex - 1];

        return {
            mentalTitle: mentalData.mental_titel,
            mainColor: knowHowData.layout.main_color,
            text: tip.text,
            title: tip.titel,
        };
    }

    getKnowHowMainData(
        mentalLinkIdentifier: MentalLinkIdentifier,
    ): MentalKnowHowMainData | undefined {
        const mentalData = this.findMentalData(mentalLinkIdentifier.mentalId);

        if (!mentalData) {
            return undefined;
        }

        const knowHowData = mentalData.know_how;

        const tips = MappingHelper.mapTips(mentalData);
        const textLinks = MappingHelper.mapKnowHowTextLinks(mentalLinkIdentifier, mentalData);

        return {
            mentalTitle: mentalData.mental_titel,
            mainColor: knowHowData.layout.main_color,
            title: knowHowData.titel,
            text: getHTMLParagraphs(knowHowData.text),
            tips,
            textLinks,
        };
    }

    getMentalFooterMenuLayout(mentalId: Identifier): MentalFooterMenuLayout {
        const found = this.data.mentals.find((mental: any) => (mental.mental_id === mentalId));

        return {
            lessonsIcon: {
                default: found.footer_menu.uebungen_inaktiv_icon,
                active: found.footer_menu.uebungen_aktiv_icon,
            },
            knowhowIcon: {
                default: found.footer_menu.knowhow_inaktiv_icon,
                active: found.footer_menu.knowhow_aktiv_icon,
            },
            activeColor: found.layout_page.header_bg_color,
        };
    }

    getMentalKnowHowPageLayout(mentalId: Identifier): PageLayout {
        const mentalData = this.findMentalData(mentalId);

        const { layout } = mentalData.know_how;
        const mentalLayout = mentalData.layout_page;

        const mainColor = layout.main_color;

        return {
            headerColor: {
                bgColor: mainColor,
                fontColor: layout.header_font_color,
            },
            bodyColor: {
                bgColor: layout.body_bg_color,
                fontColor: layout.body_font_color,
            },
            headerWaveUrl: layout.header_welle,
            footerColor: {
                bgColor: mentalLayout.footer_bg_color,
                fontColor: mentalLayout.footer_font_color,
            },
            footerWaveUrl: mentalLayout.footer_welle,
        };
    }

    getMentalPageLayout(mentalId: Identifier): PageLayout {
        const found = this.findMentalData(mentalId);

        return {
            headerColor: {
                bgColor: found.layout_page.header_bg_color,
                fontColor: found.layout_page.header_font_color,
            },
            bodyColor: {
                bgColor: found.layout_page.body_bg_color,
                fontColor: found.layout_page.body_font_color,
            },
            footerColor: {
                bgColor: found.layout_page.footer_bg_color,
                fontColor: found.layout_page.footer_font_color,
            },
            headerWaveUrl: found.layout_page.header_welle,
            footerWaveUrl: found.layout_page.footer_welle,
        };
    }

    private getPlayerLayout(mentalId: Identifier): WindowLayout {
        const found = this.findMentalData(mentalId);

        return {
            headerColor: {
                bgColor: found.player_layout.header_bg_color,
                fontColor: found.player_layout.header_font_color,
            },
            bodyColor: {
                bgColor: found.player_layout.body_bg_color,
                fontColor: found.player_layout.body_font_color,
            },
            headerWaveUrl: found.player_layout.header_welle,
        };
    }

    async loadData(): Promise<boolean> {
        try {
            const result = await axios.get(Config.dataUrl);
            this.data = result.data.response;
            return true;
        } catch {
            return false;
        }
    }
}

const api = new MentalApi();
export default api;
